import { Badge, Box, Button, HStack, Icon, Image, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react';
import { FiBookOpen, FiCheckCircle, FiSmile } from 'react-icons/fi';
import { resolveImageUrl } from '../../services/api';
import { formatDate, formatTime } from '../../utils/dateUtils';

// 電子聯絡簿貼文卡（渲染 chat_message 中 content_type='comm_book' 的訊息）。
// 資料來源：GET /chat/conversations/{id}/messages 回傳的 message.comm_book
// = { id, date, content:[{label,type,text?,photo_urls?}], photos:[url], feedback:{scores,comment,mood,health}|null,
//     signature:{signed,signed_at} }
//
// [B3] signature 欄位：後端 app/routers/chat.py _comm_book_dict() 已 join ParentSignature（見
// origin/main 635b53a「補項② 聯絡簿訊息附簽收狀態」）。簽收按鈕只在呼叫端明確傳入 onSign 時才
// 渲染（家長對話室會傳；老師端不傳，維持老師不能代簽的既有規則）。
//
// 簽收已改樂觀更新、不再等待網路，故不需要「簽收中」狀態（比照 Composer 送文字的既有處理）：
// onSign 由家長對話室負責「先把本地 signature 標成已簽名、API 失敗再回滾＋toast」，按下當下
// 這張卡就會重繪成「已簽名」，按鈕自然消失。這裡若還 await onSign 再收轉圈，等的仍是同一次
// 網路來回（實機約 5 秒），樂觀更新就完全沒有意義了。
// 連點也不必另外擋：狀態在同一次 render 就切走、按鈕已不在畫面上；真的失敗回滾後按鈕原樣回來，
// 那時再按一次本來就該當成家長主動重試。
export default function CommBookCard({ message, onImageClick, onSign }) {
    const book = message.comm_book;

    if (!book) {
        // 防呆：後端理論上 content_type='comm_book' 必帶 ref_book_id 且查得到 book；
        // 查無資料時（例如聯絡簿被刪除）顯示簡短提示，不讓整串對話崩潰。
        return (
            <Box bg="neutral.50" border="1px dashed" borderColor="neutral.300" borderRadius="xl" p={3} mb={3}>
                <Text fontSize="sm" color="neutral.400">（聯絡簿內容已無法取得）</Text>
            </Box>
        );
    }

    return (
        <Box bg="white" border="1px solid" borderColor="brand.100" borderRadius="xl" p={3} mb={3} w="100%">
            <HStack justify="space-between" mb={2}>
                <HStack spacing={1.5} color="brand.600">
                    <FiBookOpen />
                    <Text fontSize="xs" fontWeight="bold">電子聯絡簿</Text>
                </HStack>
                <Text fontSize="11px" color="neutral.400">{formatDate(book.date)} {formatTime(message.created_at)}</Text>
            </HStack>

            <VStack align="stretch" spacing={3}>
                {(book.content || []).map((block, idx) => (
                    <Box key={idx}>
                        {block.label && (
                            <Badge mb={1} colorScheme="gray" fontSize="10px">{block.label}</Badge>
                        )}
                        {block.type === 'photo' ? (
                            <HStack spacing={2} overflowX="auto" py={1}>
                                {(block.photo_urls || []).map((url, pIdx) => (
                                    <Image
                                        key={pIdx}
                                        src={resolveImageUrl(url)}
                                        h="96px"
                                        borderRadius="lg"
                                        flexShrink={0}
                                        cursor="pointer"
                                        onClick={() => onImageClick?.(resolveImageUrl(url))}
                                    />
                                ))}
                            </HStack>
                        ) : (
                            block.text && <Text fontSize="sm" whiteSpace="pre-wrap" color="neutral.700">{block.text}</Text>
                        )}
                    </Box>
                ))}

                {/* 全寬照片：點擊放大（react-zoom-pan-pinch，由上層 Room 統一管理 lightbox） */}
                {book.photos?.length > 0 && (
                    <Wrap spacing={2}>
                        {book.photos.map((url, idx) => (
                            <WrapItem key={idx} w="100%">
                                <Image
                                    src={resolveImageUrl(url)}
                                    w="100%"
                                    maxH="200px"
                                    objectFit="cover"
                                    borderRadius="lg"
                                    cursor="pointer"
                                    onClick={() => onImageClick?.(resolveImageUrl(url))}
                                />
                            </WrapItem>
                        ))}
                    </Wrap>
                )}

                {book.feedback && (
                    <Box bg="orange.50" borderRadius="lg" p={2.5}>
                        <HStack mb={1} color="orange.600">
                            <FiSmile size={14} />
                            <Text fontSize="xs" fontWeight="bold">個別回饋</Text>
                            {book.feedback.mood && <Badge colorScheme="orange" fontSize="10px">{book.feedback.mood}</Badge>}
                            {book.feedback.health && book.feedback.health !== 'Normal' && (
                                <Badge colorScheme="red" fontSize="10px">{book.feedback.health}</Badge>
                            )}
                        </HStack>
                        {book.feedback.scores && Object.keys(book.feedback.scores).length > 0 && (
                            <Wrap mb={1} spacing={1}>
                                {Object.entries(book.feedback.scores).map(([k, v]) => (
                                    <WrapItem key={k}>
                                        <Badge variant="subtle" colorScheme="gray" fontSize="10px">{k}：{String(v)}</Badge>
                                    </WrapItem>
                                ))}
                            </Wrap>
                        )}
                        {book.feedback.comment && (
                            <Text fontSize="sm" color="neutral.700">{book.feedback.comment}</Text>
                        )}
                    </Box>
                )}

                {/* [B3] 簽收狀態／簽收鈕：book.signature 沒資料時（理論上不會，防呆）整塊不顯示 */}
                {book.signature && (
                    book.signature.signed ? (
                        <HStack color="green.600" spacing={1.5}>
                            <Icon as={FiCheckCircle} boxSize={4} />
                            <Text fontSize="xs" fontWeight="bold">
                                已簽名{book.signature.signed_at ? `．${formatDate(book.signature.signed_at)} ${formatTime(book.signature.signed_at)}` : ''}
                            </Text>
                        </HStack>
                    ) : onSign ? (
                        <Button
                            size="sm"
                            colorScheme="brand"
                            borderRadius="lg"
                            alignSelf="flex-start"
                            onClick={() => onSign(book.id)}
                        >
                            我要簽名
                        </Button>
                    ) : (
                        <Text fontSize="xs" color="orange.500" fontWeight="bold">待簽名</Text>
                    )
                )}
            </VStack>
        </Box>
    );
}
